import { useEffect, useMemo, useState } from "react"
import { MessageSquare } from "lucide-react"
import { PaginationState } from "@tanstack/react-table"
import { useGetThreadsQuery } from "../../../../redux/queries"
import {
  useDateFilter,
  useDebouncedCallback,
  useUserRoles
} from "../../../hooks"
import { dateRangeToTimestamps } from "../../../utils/datetime"
import { TAB_PAGE_SIZE } from "../constants"
import TabTableSkeleton from "../tab-table-skeleton"
import SmsThreadsTable from "./sms-table"

interface Props {
  brandId?: string | number
  loid?: string
  orgId?: string | number
}

const SmsTab = ({ brandId, loid, orgId }: Props) => {
  const { isSuperAdmin } = useUserRoles()
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const {
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate
  } = useDateFilter()

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: TAB_PAGE_SIZE
  })

  const queryParams = useMemo(() => {
    const { min: updated_min } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )
    return {
      ...(!isSuperAdmin && brandId ? { brand_id: brandId } : {}),
      ...(loid ? { loid } : {}),
      ...(orgId ? { org_id: orgId } : {}),
      ...(debouncedSearch ? { name_like: debouncedSearch } : {}),
      ...(updated_min ? { updated_min } : {}),
      limit: TAB_PAGE_SIZE,
      skip: pagination.pageIndex * TAB_PAGE_SIZE,
      message_preview: "true" as const
    }
  }, [
    isSuperAdmin,
    brandId,
    loid,
    orgId,
    debouncedSearch,
    datePreset,
    customDateStart,
    customDateEnd,
    pagination.pageIndex
  ])

  const { data: threads = [], isLoading } = useGetThreadsQuery(queryParams)

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, 300)

  useEffect(() => {
    applySearch(searchInput)
  }, [searchInput, applySearch])

  // Reset page when date filter or role params change
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [datePreset, customDateStart, customDateEnd, brandId, loid, orgId])

  // Server-side pagination: RTK Query returns only the current page.
  // We estimate total count as: all pages before current are full, current may be partial.
  const hasMore = threads.length === TAB_PAGE_SIZE
  const totalRows = pagination.pageIndex * TAB_PAGE_SIZE + threads.length
  const pageCount = hasMore
    ? pagination.pageIndex + 2
    : pagination.pageIndex + 1

  if (isLoading && pagination.pageIndex === 0) {
    return <TabTableSkeleton />
  }

  if (!isLoading && !threads.length && pagination.pageIndex === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-blue-50">
          <MessageSquare className="w-7 h-7 text-blue-300" />
        </div>
        <p className="text-sm font-semibold text-neutral-600">
          No SMS conversations yet
        </p>
        <p className="text-xs text-neutral-400 max-w-xs">
          When leads start SMS conversations, their threads will appear here.
        </p>
      </div>
    )
  }

  return (
    <SmsThreadsTable
      threads={threads}
      isLoading={isLoading}
      totalRows={totalRows}
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      onSearchChange={setSearchInput}
      datePreset={datePreset}
      customDateStart={customDateStart}
      customDateEnd={customDateEnd}
      onDateChange={handleDateChange}
      onClearDate={handleClearDate}
    />
  )
}

export default SmsTab
